import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, forkJoin, MonoTypeOperatorFunction, of, Subject, takeUntil } from 'rxjs';
import { OpeningDay, OpeningShift, PointOfSaleService } from '../../../../../services/pos';
import { PetServices } from '../../../../../services/petservices';
import { PetsService } from '../../../../../services/pets';
import { EmployeesService } from '../../../../../services/employees';
import { UserService } from '../../../../../services/user';
import { OrdersService } from '../../../../../services/orders';
import { FinancialManagerService } from '../../../../../services/financial-manager';
import { LanguageServiceService } from '../../../../../services/language-service.service';
import { HomeService } from '../../../../../services/home.service';
import { CartService } from '../../../../../services/cart';

const IMAGE_BASE_URL = 'https://backendproduction.blueboxpet.com/storage/app/public/';

interface ClinicShiftUser {
  id: number;
  name?: string;
  name_en?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  [key: string]: any;
}

interface ClinicCalendarShift extends OpeningShift {
  user_ids?: number[];
  users?: ClinicShiftUser[];
  disabled_users?: ClinicShiftUser[];
  disabled_user_ids?: number[];
  source?: 'base' | 'emergency' | 'mixed';
}

interface DaySchedule {
  day: OpeningDay;
  labelAr: string;
  is_open: boolean;
  shifts: ClinicCalendarShift[];
}

interface MonthCalendarCell {
  date: Date;
  dayNumber: number;
  dayLabel: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  is_open: boolean;
  shifts: ClinicCalendarShift[];
  bookings: ClinicCalendarBooking[];
  vaccinations: ClinicCalendarVaccination[];
  orders: ClinicCalendarOrder[];
  ordersCount: number;
  ordersTotalMoney: number;
}

interface ClinicCalendarBooking {
  id: number;
  clientName: string;
  serviceName: string;
  phone: string;
  status: number;
  date: Date;
}

interface ClinicCalendarVaccination {
  id: number;
  petId: number | null;
  vaccineName: string;
  petName: string;
  date: Date;
}

interface ClinicCalendarOrder {
  id: number;
  clientName: string;
  phone: string;
  total: number;
  status: any;
  date: Date;
  raw: any;
}

interface ClinicInvoiceItem {
  name: string;
  quantity: number;
  price: number;
  lineTotal: number;
}

interface ClinicFinancialRecord {
  amount: number;
  date: Date;
}

type ClinicMonthlyPopupType = 'bookings' | 'vaccinations' | 'shifts' | 'orders';
type ClinicQuickActionModal = 'order' | 'booking' | 'shifts' | 'accounts';
type CalendarCartItemType = 'product' | 'service';

@Component({
  selector: 'app-clinic-calander',
  templateUrl: './clinic-calander.component.html',
  styleUrl: './clinic-calander.component.scss',
})
export class ClinicCalanderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private realTimeRefreshId: ReturnType<typeof setInterval> | null = null;
  private readonly seenCounterKeys = new Set<string>();
  private readonly playedBookingAlertKeys = new Set<string>();
  private readonly seenCounterStorageKey = 'clinicCalendarSeenCounters';
  private bookingAlertAudio: HTMLAudioElement | null = null;

  isAr = false;
  isFr = false;
  isSubAdmin = false;
  currentLanguage = 'en';
  labels = this.getLabels();

  loading = false;
  contentReady = true;
  serverError = '';
  warehouseId: string | number | null = null;
  clinicName = '';
  clinicLocation = '';
  warehouseTotalValue = 0;
  stockSearchKey = '';
  stockSearchLoading = false;
  stockSearchTouched = false;
  stockSearchResults: any[] = [];
  cartPopUp = false;
  cartItems: any[] = [];
  cartItemCount = 0;

  currentMonth = new Date();
  calendarDays: MonthCalendarCell[] = [];
  bookings: ClinicCalendarBooking[] = [];
  vaccinations: ClinicCalendarVaccination[] = [];
  ordersTotalsByDay = new Map<string, number>();
  ordersByDay = new Map<string, ClinicCalendarOrder[]>();
  ordersMoneyByDay = new Map<string, number>();
  monthlyExpenses: ClinicFinancialRecord[] = [];
  selectedDay: MonthCalendarCell | null = null;
  selectedPopupType: 'bookings' | 'vaccinations' | 'shifts' | 'orders' | null = null;
  selectedMonthlyPopupType: ClinicMonthlyPopupType | null = null;
  selectedQuickActionModal: ClinicQuickActionModal | null = null;
  selectedInvoiceOrder: ClinicCalendarOrder | null = null;
  showDeferredPopup = false;
  selectedBooking: ClinicCalendarBooking | null = null;
  selectedShiftUserActionKey: string | null = null;
  deferredBookingDate = '';
  isUpdatingStatus = false;
  weekDays: string[] = [];

  private readonly dayOrder: OpeningDay[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  private readonly dayLabelsAr: Record<OpeningDay, string> = {
    sunday: '\u0627\u0644\u0623\u062d\u062f',
    monday: '\u0627\u0644\u0625\u062b\u0646\u064a\u0646',
    tuesday: '\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621',
    wednesday: '\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621',
    thursday: '\u0627\u0644\u062e\u0645\u064a\u0633',
    friday: '\u0627\u0644\u062c\u0645\u0639\u0629',
    saturday: '\u0627\u0644\u0633\u0628\u062a',
  };

  private readonly dayLabelsEn: Record<OpeningDay, string> = {
    sunday: 'Sunday',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
  };

  private readonly dayLabelsFr: Record<OpeningDay, string> = {
    sunday: 'Dimanche',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
  };

  private schedule: DaySchedule[] = [];
  private readonly usersMap = new Map<number, ClinicShiftUser>();
  private readonly emergencyShiftsByDate = new Map<string, ClinicCalendarShift[]>();
  private activeCalendarLoadingRequests = 0;

  constructor(
    private route: ActivatedRoute,
    private posService: PointOfSaleService,
    private petServices: PetServices,
    private petsService: PetsService,
    private employeesService: EmployeesService,
    private userService: UserService,
    private ordersService: OrdersService,
    private financialService: FinancialManagerService,
    private languageService: LanguageServiceService,
    private homeService: HomeService,
    private cartService: CartService
  ) {}

  ngOnInit(): void {
    this.loadUserRole();
    this.applyLanguage(localStorage.getItem('language') || 'en');
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        this.applyLanguage(language);
        this.weekDays = this.dayOrder.map((day) => this.getDayLabel(day));
        this.buildMonthCalendar();
      });

    this.loadSeenCounterKeys();
    this.weekDays = this.dayOrder.map((day) => this.getDayLabel(day));
    this.schedule = this.getEmptySchedule();

    const today = new Date();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.warehouseId = this.resolveWarehouseId();
    this.buildMonthCalendar();

    if (!this.warehouseId) {
      this.serverError = this.labels.noClinicId;
      this.contentReady = false;
      return;
    }

    this.refreshCalendarData();
    this.loadClinicInfo();
    this.loadWarehouseEmployees();
    this.setupCartSubscriptions();
    this.startRealTimeRefresh();
  }

  ngOnDestroy(): void {
    if (this.realTimeRefreshId) {
      clearInterval(this.realTimeRefreshId);
    }

    this.stopBookingAlertSound();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.stopBookingAlertSound();
  }

  refreshCalendarData(): void {
    this.loadOpeningHours();
    this.loadBookings();
    this.loadVaccinations();
    this.loadOrders();
    this.loadFinancialExpenses();
  }

  private trackCalendarLoading<T>(): MonoTypeOperatorFunction<T> {
    this.beginCalendarLoading();
    return finalize(() => this.endCalendarLoading());
  }

  private maybeTrackCalendarLoading<T>(showLoading: boolean): MonoTypeOperatorFunction<T> {
    return showLoading ? this.trackCalendarLoading<T>() : (source) => source;
  }

  private beginCalendarLoading(): void {
    this.activeCalendarLoadingRequests += 1;
    this.contentReady = true;
  }

  private endCalendarLoading(): void {
    this.activeCalendarLoadingRequests = Math.max(0, this.activeCalendarLoadingRequests - 1);

    if (this.activeCalendarLoadingRequests === 0) {
      this.contentReady = false;
    }
  }

  private loadUserRole(): void {
    this.userService.getAccountType()
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isSubAdmin = String(data?.user_type) === '6';

          if (this.isSubAdmin) {
            this.closeRestrictedPanels();
          }
        },
        error: () => {
          this.isSubAdmin = false;
        },
      });
  }

  private closeRestrictedPanels(): void {
    if (this.selectedPopupType === 'shifts') {
      this.closeDayPopup();
    }

    if (this.selectedMonthlyPopupType === 'shifts') {
      this.closeMonthlyPopup();
    }

    if (this.selectedQuickActionModal === 'shifts' || this.selectedQuickActionModal === 'accounts') {
      this.closeQuickActionModal();
    }
  }

  private canViewClinicFinancialAndShiftControls(): boolean {
    return !this.isSubAdmin;
  }

  private setupCartSubscriptions(): void {
    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cart) => {
        this.cartItems = cart;
      });

    this.cartService.cartCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.cartItemCount = count;
      });
  }

  private normalizeStockSearchResults(response: any, itemType: CalendarCartItemType): any[] {
    let rows: any[] = [];

    if (Array.isArray(response)) rows = response;
    else if (Array.isArray(response?.data)) rows = response.data;
    else if (Array.isArray(response?.stocks?.data)) rows = response.stocks.data;
    else if (Array.isArray(response?.stocks)) rows = response.stocks;
    else if (Array.isArray(response?.services?.data)) rows = response.services.data;
    else if (Array.isArray(response?.services)) rows = response.services;
    else if (Array.isArray(response?.items)) rows = response.items;

    return rows.map((item) => ({
      ...item,
      calendarItemType: itemType,
      cartItemType: itemType,
    }));
  }

  openQuickActionModal(type: ClinicQuickActionModal): void {
    if (this.isSubAdmin && (type === 'shifts' || type === 'accounts')) return;

    this.closeDayPopup();
    this.closeMonthlyPopup();
    this.closeOrderInvoicePopup();
    this.selectedQuickActionModal = type;
  }

  closeQuickActionModal(): void {
    this.selectedQuickActionModal = null;
    this.refreshCalendarData();
  }

  canShowStockSearch(): boolean {
    return Number(this.warehouseTotalValue || 0) !== 0 && !!this.warehouseId;
  }

  searchStock(): void {
    const searchValue = this.stockSearchKey.trim();
    const warehouseId = Number(this.warehouseId || localStorage.getItem('posId'));

    if (!searchValue || !warehouseId) {
      this.clearStockSearch();
      return;
    }

    this.stockSearchLoading = true;
    this.stockSearchTouched = true;

    forkJoin({
      products: this.homeService.searchByNameAndWarehouse(searchValue, warehouseId).pipe(
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      ),
      services: this.petServices.searchServices(warehouseId, searchValue, 1, 10).pipe(
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      ),
    })
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: ({ products, services }) => {
          this.stockSearchResults = [
            ...this.normalizeStockSearchResults(products, 'product'),
            ...this.normalizeStockSearchResults(services, 'service'),
          ];
          this.stockSearchLoading = false;
        },
        error: () => {
          this.stockSearchResults = [];
          this.stockSearchLoading = false;
        },
      });
  }

  clearStockSearch(): void {
    this.stockSearchKey = '';
    this.stockSearchTouched = false;
    this.stockSearchResults = [];
    this.stockSearchLoading = false;
  }

  addStockToCart(item: any): void {
    const cartItem = this.normalizeStockCartItem(item);
    if (!cartItem.id) return;

    this.cartService.addOrToggleItem(cartItem, this.getCalendarItemType(item));
  }

  removeFromCart(item: any): void {
    this.cartService.removeItem(this.normalizeStockCartItem(item));

    if (this.cartService.getCart().length === 0) {
      this.cartPopUp = false;
    }
  }

  isInCart(item: any): boolean {
    const stockId = this.getStockCartId(item);

    return this.cartItems.some(
      (cartItem) =>
        String(this.getStockCartId(cartItem)) === String(stockId) &&
        this.getCalendarItemType(cartItem) === this.getCalendarItemType(item)
    );
  }

  cartPopUpOpen(): void {
    if (this.cartItemCount > 0) {
      this.cartPopUp = true;
    }
  }

  closeCartPopup(): void {
    this.cartPopUp = false;
  }

  quickOrder(): void {
    if (this.cartItems.length === 0) return;

    this.closeCartPopup();
    this.openQuickActionModal('order');
  }

  getStockDisplayName(item: any): string {
    if (this.isAr) {
      return item?.name_ar || item?.name || item?.name_en || this.labels.productFallback;
    }

    return item?.name_en || item?.name || item?.name_ar || this.labels.productFallback;
  }

  getStockImage(item: any): string {
    const image = item?.image_url || item?.image || item?.photo || item?.thumbnail;
    if (!image) return '/assets/logo-01.png';
    if (/^https?:\/\//i.test(String(image))) return image;

    return IMAGE_BASE_URL + image;
  }

  getStockQuantity(item: any): number {
    return Number(item?.quantity ?? item?.qty ?? item?.stock_quantity ?? 0);
  }

  getStockPrice(item: any): number {
    return Number(item?.price ?? item?.sell_price ?? item?.selling_price ?? item?.pricea ?? 0);
  }

  getStockCartId(item: any): any {
    return item?.id ?? item?.stock_id ?? item?.stockId ?? item?.stock?.id ?? item?.product_id ?? item?.productId;
  }

  getCalendarItemType(item: any): CalendarCartItemType {
    return item?.calendarItemType === 'service' || item?.cartItemType === 'service' ? 'service' : 'product';
  }

  getCalendarItemTypeLabel(item: any): string {
    return this.getCalendarItemType(item) === 'service' ? this.labels.service : this.labels.product;
  }

  getCalendarItemTypeBadgeClass(item: any): string {
    return this.getCalendarItemType(item) === 'service' ? 'bb-badge-success' : 'bb-badge-primary';
  }

  private normalizeStockCartItem(item: any): any {
    return {
      ...item,
      id: this.getStockCartId(item),
      cartItemType: this.getCalendarItemType(item),
    };
  }

  getQuickActionModalTitle(): string {
    if (this.selectedQuickActionModal === 'order') return this.labels.newOrder;
    if (this.selectedQuickActionModal === 'booking') return this.labels.newBooking;
    if (this.selectedQuickActionModal === 'shifts') return this.labels.supply;
    if (this.selectedQuickActionModal === 'accounts') return this.labels.accounts;

    return '';
  }

  loadClinicInfo(): void {
    if (!this.warehouseId) return;

    this.posService
      .getPOS(String(this.warehouseId))
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.clinicName = this.extractClinicName(res);
          this.clinicLocation = this.extractClinicLocation(res);
          this.warehouseTotalValue = this.extractWarehouseTotalValue(res);

          if (this.warehouseTotalValue === 0) {
            this.loadWarehouseTotalValueFromList();
          }
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  loadOpeningHours(): void {
    if (!this.warehouseId) return;

    this.loading = true;
    this.serverError = '';

    this.posService
      .getOpeningHours(this.warehouseId)
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.schedule = this.normalizeOpeningHours(res);
          this.buildMonthCalendar();
          this.loadMonthEffectiveOpeningHours();
        },
        error: (err) => {
          this.loading = false;
          this.serverError = err?.error?.message || this.labels.openingHoursError;
        },
      });
  }

  private loadMonthEffectiveOpeningHours(): void {
    if (!this.warehouseId) {
      this.loading = false;
      return;
    }

    const dateKeys = this.getCurrentMonthDateKeys();

    if (!dateKeys.length) {
      this.loading = false;
      return;
    }

    forkJoin(
      dateKeys.map((date) =>
        this.posService.getEffectiveOpeningHour(this.warehouseId as string | number, date).pipe(
          catchError(() => of(null))
        )
      )
    )
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          this.emergencyShiftsByDate.clear();

          responses.forEach((res: any, index: number) => {
            if (!res || this.extractEffectiveSource(res) !== 'override') return;

            const effectiveDay = this.extractEffectiveOpeningHour(res, dateKeys[index]);
            if (!effectiveDay.is_open) return;

            const shifts = this.extractCalendarShiftsFromRow(effectiveDay)
              .filter((shift: ClinicCalendarShift) => shift.start && shift.end)
              .map((shift: ClinicCalendarShift) => ({
                ...shift,
                source: 'emergency' as const,
              }));

            if (shifts.length) {
              this.emergencyShiftsByDate.set(dateKeys[index], shifts);
            }
          });

          this.buildMonthCalendar();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  loadBookings(showLoading = true): void {
    const warehouseId = Number(this.warehouseId || localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.petServices
      .getAllBookings(1, 500, warehouseId)
      .pipe(this.maybeTrackCalendarLoading(showLoading), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          const normalizedBookings: ClinicCalendarBooking[] = rows
            .map((booking: any) => this.normalizeBooking(booking))
            .filter(
              (booking: ClinicCalendarBooking | null): booking is ClinicCalendarBooking =>
                !!booking
            );

          this.bookings = normalizedBookings.sort(
            (a: ClinicCalendarBooking, b: ClinicCalendarBooking) =>
              a.date.getTime() - b.date.getTime()
          );

          this.rebuildCalendarAndPopup();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  loadVaccinations(showLoading = true): void {
    const warehouseId = Number(this.warehouseId || localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.petsService
      .getUpcomingVaccinations(warehouseId)
      .pipe(this.maybeTrackCalendarLoading(showLoading), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows = this.extractArrayResponse(res);
          const normalizedVaccinations: ClinicCalendarVaccination[] = rows
            .map((vaccination: any) => this.normalizeVaccination(vaccination))
            .filter(
              (
                vaccination: ClinicCalendarVaccination | null
              ): vaccination is ClinicCalendarVaccination => !!vaccination
            );

          this.vaccinations = normalizedVaccinations.sort(
            (a: ClinicCalendarVaccination, b: ClinicCalendarVaccination) =>
              a.date.getTime() - b.date.getTime()
          );

          this.rebuildCalendarAndPopup();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  loadOrders(showLoading = true): void {
    const warehouseId = Number(this.warehouseId || localStorage.getItem('posId'));
    if (!warehouseId) return;

    this.ordersService
      .getOrdersTotalsByDate({
        warehouse_id: warehouseId,
        month: this.currentMonth.getMonth() + 1,
        year: this.currentMonth.getFullYear(),
        withorders: 1,
      })
      .pipe(this.maybeTrackCalendarLoading(showLoading), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const orders = this.extractOrdersRows(res);

          if (orders.length) {
            this.setOrdersFromRows(orders);
            this.rebuildCalendarAndPopup();
            return;
          }

          const totals = this.normalizeOrdersTotalsByDate(res);

          if (!totals.size) {
            this.loadOrdersFromWarehouseList(warehouseId, 1, [], showLoading);
            return;
          }

          this.ordersTotalsByDay = totals;
          this.ordersByDay = new Map<string, ClinicCalendarOrder[]>();
          this.ordersMoneyByDay = new Map<string, number>();
          this.rebuildCalendarAndPopup();
        },
        error: (err) => {
          console.error(err);
          this.loadOrdersFromWarehouseList(warehouseId, 1, [], showLoading);
        },
      });
  }

  loadFinancialExpenses(): void {
    const warehouseId = Number(this.warehouseId || localStorage.getItem('posId'));
    if (!warehouseId) return;

    const month = `${this.currentMonth.getFullYear()}-${String(this.currentMonth.getMonth() + 1).padStart(2, '0')}`;

    this.financialService
      .getOutbalanceByMonth(month, warehouseId)
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.monthlyExpenses = this.extractArrayResponse(res)
            .map((record) => this.normalizeFinancialRecord(record))
            .filter((record: ClinicFinancialRecord | null): record is ClinicFinancialRecord => !!record);
        },
        error: (err) => {
          console.error(err);
          this.monthlyExpenses = [];
        },
      });
  }

  goToday(): void {
    const today = new Date();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.buildMonthCalendar();
    this.loadMonthEffectiveOpeningHours();
    this.loadOrders();
    this.loadFinancialExpenses();
  }

  goPreviousMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.buildMonthCalendar();
    this.loadMonthEffectiveOpeningHours();
    this.loadOrders();
    this.loadFinancialExpenses();
  }

  goNextMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.buildMonthCalendar();
    this.loadMonthEffectiveOpeningHours();
    this.loadOrders();
    this.loadFinancialExpenses();
  }

  getMonthTitle(): string {
    return new Intl.DateTimeFormat(this.getLocale(), {
      month: 'long',
      year: 'numeric',
    }).format(this.currentMonth);
  }

  getPageTitle(): string {
    return this.clinicName || this.labels.title;
  }

  getPageSubtitle(): string {
    return this.clinicLocation || this.labels.subtitle;
  }

  getCurrentMonthShiftsTotal(): number {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth && day.is_open)
      .reduce((total, day) => total + day.shifts.length, 0);
  }

  getCurrentMonthBookingsTotal(): number {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth)
      .reduce((total, day) => total + day.bookings.length, 0);
  }

  getCurrentMonthVaccinationsTotal(): number {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth)
      .reduce((total, day) => total + day.vaccinations.length, 0);
  }

  getCurrentMonthOrdersTotal(): number {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth)
      .reduce((total, day) => total + day.ordersCount, 0);
  }

  getCurrentMonthOrdersMoneyTotal(): number {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth)
      .reduce((total, day) => total + day.ordersTotalMoney, 0);
  }

  formatNumber(value: number | string): string {
    const normalizedValue = String(value ?? '0');

    if (!this.isAr) {
      return normalizedValue;
    }

    const arabicDigits: Record<string, string> = {
      '0': '\u0660',
      '1': '\u0661',
      '2': '\u0662',
      '3': '\u0663',
      '4': '\u0664',
      '5': '\u0665',
      '6': '\u0666',
      '7': '\u0667',
      '8': '\u0668',
      '9': '\u0669',
    };

    return normalizedValue.replace(/\d/g, (digit) => arabicDigits[digit]);
  }

  formatMoney(value: number | string): string {
    const numberValue = Number(value || 0);

    const formattedValue = numberValue.toLocaleString(this.getLocale(), {
      maximumFractionDigits: 2,
    });

    return this.isAr ? formattedValue : formattedValue;
  }

  formatShift(shift: ClinicCalendarShift): string {
    return `${this.formatTime(shift.start)} - ${this.formatTime(shift.end)}`;
  }

  isEmergencyShift(shift: ClinicCalendarShift): boolean {
    return shift.source === 'emergency' || shift.source === 'mixed';
  }

  getEmergencyShiftLabel(): string {
    if (this.isAr) return '\u062a\u063a\u064a\u064a\u0631 \u0627\u0636\u0637\u0631\u0627\u0631\u064a';
    if (this.isFr) return "Changement d'urgence";

    return 'Emergency change';
  }

  formatBookingTime(booking: ClinicCalendarBooking): string {
    return this.formatTimeFromDate(booking.date);
  }

  formatOrderTime(order: ClinicCalendarOrder): string {
    return this.formatTimeFromDate(order.date);
  }

  formatOrderDateTime(order: ClinicCalendarOrder): string {
    return this.formatDateTime(order.date);
  }

  formatDateTime(dateValue: Date): string {
    const date = new Intl.DateTimeFormat(this.getLocale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(dateValue);

    return `${date} - ${this.formatTimeFromDate(dateValue)}`;
  }

  private formatTimeFromDate(date: Date): string {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = this.isAr
      ? hours < 12
        ? '\u0635'
        : '\u0645'
      : hours < 12
        ? 'AM'
        : 'PM';
    const hour12 = hours % 12 || 12;

    return `${this.formatNumber(String(hour12).padStart(2, '0'))}:${this.formatNumber(String(minutes).padStart(2, '0'))} ${period}`;
  }

  openBookingsPopup(day: MonthCalendarCell, event?: Event): void {
    event?.stopPropagation();
    this.markBookingsSeen(day.bookings);
    this.selectedDay = day;
    this.selectedPopupType = 'bookings';
  }

  openVaccinationsPopup(day: MonthCalendarCell): void {
    this.markCounterSeen(day, 'vaccinations');
    this.selectedDay = day;
    this.selectedPopupType = 'vaccinations';
  }

  openOrdersPopup(day: MonthCalendarCell): void {
    this.selectedDay = day;
    this.selectedPopupType = 'orders';
    this.closeOrderInvoicePopup();
  }

  openOrderInvoicePopup(order: ClinicCalendarOrder, event?: Event): void {
    event?.stopPropagation();
    this.selectedInvoiceOrder = order;
  }

  closeOrderInvoicePopup(): void {
    this.selectedInvoiceOrder = null;
  }

  openShiftsPopup(day: MonthCalendarCell): void {
    if (!this.canViewClinicFinancialAndShiftControls()) return;

    this.selectedDay = day;
    this.selectedPopupType = 'shifts';
  }

  openMonthlyPopup(type: ClinicMonthlyPopupType, event?: Event): void {
    event?.stopPropagation();
    if (type === 'shifts' && !this.canViewClinicFinancialAndShiftControls()) return;

    this.closeDayPopup();
    if (type === 'bookings') {
      this.markBookingsSeen(this.getCurrentMonthBookings());
    }
    this.selectedMonthlyPopupType = type;
  }

  closeMonthlyPopup(): void {
    this.selectedMonthlyPopupType = null;
    this.selectedShiftUserActionKey = null;
    this.closeOrderInvoicePopup();
  }

  closeDayPopup(): void {
    this.selectedDay = null;
    this.selectedPopupType = null;
    this.closeOrderInvoicePopup();
    this.selectedShiftUserActionKey = null;
    this.closeDeferredPopup();
  }

  getMonthlyPopupTitle(): string {
    if (!this.selectedMonthlyPopupType) return '';

    const titleMap: Record<ClinicMonthlyPopupType, string> = {
      bookings: this.labels.bookings,
      vaccinations: this.labels.vaccinations,
      shifts: this.labels.shifts,
      orders: this.labels.orders,
    };

    return titleMap[this.selectedMonthlyPopupType];
  }

  getCurrentMonthDaysWithShifts(): MonthCalendarCell[] {
    return this.calendarDays.filter(
      (day) => day.isCurrentMonth && day.is_open && day.shifts.length > 0
    );
  }

  getCurrentMonthBookings(): ClinicCalendarBooking[] {
    return this.bookings
      .filter((booking) => this.isCurrentMonthDate(booking.date))
      .sort((first, second) => first.date.getTime() - second.date.getTime());
  }

  getCurrentMonthVaccinations(): ClinicCalendarVaccination[] {
    return this.vaccinations
      .filter((vaccination) => this.isCurrentMonthDate(vaccination.date))
      .sort((first, second) => first.date.getTime() - second.date.getTime());
  }

  getCurrentMonthOrders(): ClinicCalendarOrder[] {
    return this.calendarDays
      .filter((day) => day.isCurrentMonth)
      .flatMap((day) => day.orders)
      .sort((first, second) => second.date.getTime() - first.date.getTime());
  }

  getInvoiceItems(order: ClinicCalendarOrder): ClinicInvoiceItem[] {
    const raw = order.raw || {};
    const itemValues = this.parseArrayField(
      raw.items ??
        raw.item ??
        raw.order_items ??
        raw.orderItems ??
        raw.products ??
        raw.product ??
        raw.product_name ??
        raw.product_names ??
        raw.item_name ??
        raw.names ??
        raw.stock
    );
    const quantityValues = this.parseArrayField(
      raw.quantity ?? raw.quantities ?? raw.qty ?? raw.qtys ?? raw.product_quantity
    );
    const priceValues = this.parseArrayField(
      raw.price ?? raw.prices ?? raw.product_price ?? raw.sell_price ?? raw.selling_price
    );
    const totalValues = this.parseArrayField(
      raw.item_total ?? raw.item_totals ?? raw.line_total ?? raw.line_totals ?? raw.total_price
    );

    const maxLength = Math.max(itemValues.length, quantityValues.length, priceValues.length, totalValues.length);

    return Array.from({ length: maxLength })
      .map((_, index) => {
        const item = itemValues[index];
        const name =
          this.extractItemName(item) ||
          this.extractItemName(raw.products?.[index]) ||
          this.extractItemName(raw.items?.[index]) ||
          `${this.labels.productFallback} ${this.formatNumber(index + 1)}`;
        const quantity = this.extractItemNumber(item, ['quantity', 'qty', 'count']) || this.toNumber(quantityValues[index]) || 1;
        const price =
          this.extractItemNumber(item, ['price', 'sell_price', 'selling_price', 'unit_price']) ||
          this.toNumber(priceValues[index]);
        const explicitTotal =
          this.extractItemNumber(item, ['total', 'line_total', 'subtotal']) || this.toNumber(totalValues[index]);
        const lineTotal = explicitTotal || quantity * price;

        return { name, quantity, price, lineTotal };
      })
      .filter((item) => item.name.trim().length > 0);
  }

  getOrderSubtotal(order: ClinicCalendarOrder): number {
    const itemsTotal = this.getInvoiceItems(order).reduce((total, item) => total + item.lineTotal, 0);
    return itemsTotal || order.total;
  }

  getOrderDiscount(order: ClinicCalendarOrder): number {
    return this.toNumber(order.raw?.discount ?? order.raw?.discount_value ?? order.raw?.offer_discount);
  }

  getOrderDeliveryFees(order: ClinicCalendarOrder): number {
    return this.toNumber(order.raw?.delivery_fees ?? order.raw?.delivery_fee ?? order.raw?.shipping ?? order.raw?.shipping_fees);
  }

  getOrderPaidAmount(order: ClinicCalendarOrder): number {
    return this.toNumber(order.raw?.paid ?? order.raw?.paid_amount ?? order.raw?.amount_paid);
  }

  getOrderRemainingAmount(order: ClinicCalendarOrder): number {
    return this.toNumber(order.raw?.remaining ?? order.raw?.remaining_amount ?? order.raw?.rest);
  }

  getSelectedDayTitle(): string {
    if (!this.selectedDay) return '';

    return this.getSelectedDayTitleForDate(this.selectedDay.date);
  }

  getSelectedDayTitleForDate(date: Date): string {
    return new Intl.DateTimeFormat(this.getLocale(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  getBookingStatusClass(status: number): string {
    const statusMap: Record<number, string> = {
      0: 'pending',
      1: 'approved',
      2: 'rejected',
      3: 'deferred',
      4: 'completed',
    };

    return statusMap[Number(status)] || 'pending';
  }

  getBookingStatusLabel(status: number): string {
    const labelMap: Record<number, string> = {
      0: this.labels.pending,
      1: this.labels.approved,
      2: this.labels.rejected,
      3: this.labels.deferred,
      4: this.labels.completed,
    };

    return labelMap[Number(status)] || labelMap[0];
  }

  isFinalStatus(status: any): boolean {
    return [2, 4].includes(Number(status));
  }

  setBookingStatus(booking: ClinicCalendarBooking, status: number): void {
    if (this.isFinalStatus(booking.status)) return;
    this.acknowledgeBookingAlert(booking);

    if (status === 3) {
      this.openDeferredDatePopup(booking);
      return;
    }

    const oldStatus = booking.status;
    booking.status = status;

    this.isUpdatingStatus = true;
    this.petServices
      .updateBookingStatus(booking.id, { status })
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
      next: (res: any) => {
        booking.status = Number(res?.data?.status ?? status);
        this.updateStoredBooking(booking);
        this.isUpdatingStatus = false;
      },
      error: (err) => {
        console.error(err);
        booking.status = oldStatus;
        this.isUpdatingStatus = false;
        alert(err?.error?.message || this.labels.updateStatusError);
      },
    });
  }

  openDeferredDatePopup(booking: ClinicCalendarBooking): void {
    if (this.isFinalStatus(booking.status)) return;
    this.acknowledgeBookingAlert(booking);

    this.selectedBooking = booking;
    this.deferredBookingDate = this.formatDateTimeForInput(booking.date);
    this.showDeferredPopup = true;
  }

  closeDeferredPopup(): void {
    if (this.isUpdatingStatus) return;

    this.showDeferredPopup = false;
    this.selectedBooking = null;
    this.deferredBookingDate = '';
  }

  submitDeferredDate(): void {
    if (!this.selectedBooking || !this.deferredBookingDate) return;

    const booking = this.selectedBooking;
    const oldStatus = booking.status;
    const oldDate = booking.date;
    const cleanDateTime = this.convertDateTimeLocalToMysql(this.deferredBookingDate);
    const nextDate = this.parseBookingDate(cleanDateTime);

    if (!nextDate) return;

    booking.status = 3;
    booking.date = nextDate;

    this.isUpdatingStatus = true;
    this.petServices
      .updateBookingStatus(booking.id, {
        status: 3,
        deferred_booking_date: cleanDateTime,
      })
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const responseDate = this.parseBookingDate(
            res?.data?.deferred_booking_date || cleanDateTime
          );

          booking.status = Number(res?.data?.status ?? 3);
          booking.date = responseDate || nextDate;
          this.updateStoredBooking(booking);
          this.isUpdatingStatus = false;
          this.closeDeferredPopup();
        },
        error: (err) => {
          console.error(err);
          booking.status = oldStatus;
          booking.date = oldDate;
          this.isUpdatingStatus = false;
          alert(err?.error?.message || this.labels.updateDeferredError);
        },
      });
  }

  deleteBooking(booking: ClinicCalendarBooking): void {
    if (!confirm(this.labels.deleteConfirm)) return;
    this.acknowledgeBookingAlert(booking);

    this.petServices
      .deleteBooking(booking.id)
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
      next: () => {
        this.bookings = this.bookings.filter((item) => item.id !== booking.id);
        this.rebuildCalendarAndPopup();
      },
      error: (err) => {
        console.error(err);
        alert(err?.error?.message || this.labels.deleteError);
      },
    });
  }

  trackByDay(index: number, day: MonthCalendarCell): string {
    return `${index}-${day.date.toISOString()}`;
  }

  isCounterAlerting(
    day: MonthCalendarCell,
    type: 'bookings' | 'vaccinations'
  ): boolean {
    if (type === 'bookings') {
      return day.bookings.some((booking) => !this.seenCounterKeys.has(this.getBookingAlertKey(booking)));
    }

    const count = day.vaccinations.length;

    return count > 0 && !this.seenCounterKeys.has(this.getCounterKey(day, type));
  }

  isMonthlyBookingsAlerting(): boolean {
    return this.getCurrentMonthBookings().some((booking) => {
      return !this.seenCounterKeys.has(this.getBookingAlertKey(booking));
    });
  }

  acknowledgeBookingAlert(booking?: ClinicCalendarBooking, event?: Event): void {
    event?.stopPropagation();

    if (booking) {
      this.markBookingsSeen([booking]);
      return;
    }

    this.markCurrentMonthBookingsSeen();
  }

  getShiftUserNames(shift: ClinicCalendarShift): string[] {
    const users = this.getShiftUsers(shift);

    return users
      .map((user) => this.getUserName(user))
      .filter((name): name is string => !!name);
  }

  getShiftUsersForView(shift: ClinicCalendarShift): ClinicShiftUser[] {
    const disabledUsers = this.getDisabledShiftUsers(shift).map((user) => ({
      ...user,
      __shiftDisabled: true,
    }));

    return [...disabledUsers, ...this.getShiftUsers(shift)];
  }

  getShiftUsersCount(shift: ClinicCalendarShift): number {
    return this.getShiftUsers(shift).length;
  }

  isShiftUserDisabled(user: ClinicShiftUser): boolean {
    return Boolean(user?.['__shiftDisabled']);
  }

  getShiftSalesTotal(date: Date, shift: ClinicCalendarShift): number {
    const range = this.getShiftDateRange(date, shift);
    if (!range) return 0;

    return this.getOrdersForDate(date)
      .filter((order) => this.isDateInRange(order.date, range.start, range.end))
      .reduce((total, order) => total + order.total, 0);
  }

  getShiftExpensesTotal(date: Date, shift: ClinicCalendarShift): number {
    const range = this.getShiftDateRange(date, shift);
    if (!range) return 0;

    return this.monthlyExpenses
      .filter((expense) => this.isDateInRange(expense.date, range.start, range.end))
      .reduce((total, expense) => total + expense.amount, 0);
  }

  getShiftProfitTotal(date: Date, shift: ClinicCalendarShift): number {
    return this.getShiftSalesTotal(date, shift) - this.getShiftExpensesTotal(date, shift);
  }

  getShiftUserName(user: ClinicShiftUser): string {
    return this.getUserName(user);
  }

  toggleShiftUserActions(
    shift: ClinicCalendarShift,
    user: ClinicShiftUser,
    event: Event
  ): void {
    event.stopPropagation();
    const key = this.getShiftUserActionKey(shift, user);
    this.selectedShiftUserActionKey =
      this.selectedShiftUserActionKey === key ? null : key;
  }

  isShiftUserActionsOpen(shift: ClinicCalendarShift, user: ClinicShiftUser): boolean {
    return this.selectedShiftUserActionKey === this.getShiftUserActionKey(shift, user);
  }

  getEmployeeDetailsQueryParams(user: ClinicShiftUser): Record<string, any> {
    return {
      id: user.id,
      name: this.getUserName(user),
      job_title: user?.['job_title'] || '',
      salary: user?.['salary'] || '',
    };
  }

  getEmployeeSalesQueryParams(user: ClinicShiftUser): Record<string, any> {
    return {
      employee_id: user.id,
    };
  }

  trackByShift(index: number, shift: ClinicCalendarShift): string {
    return `${index}-${shift.start}-${shift.end}`;
  }

  trackByShiftUser(index: number, user: ClinicShiftUser): number {
    return Number(user.id || index);
  }

  private loadWarehouseEmployees(): void {
    const warehouseId = Number(this.warehouseId);
    if (!warehouseId || Number.isNaN(warehouseId)) return;

    this.employeesService
      .getEmployeesByWarehouse(warehouseId, 1, 500)
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.extractEmployeesResponse(res).forEach((user) => this.registerUser(user));
          this.rebuildCalendarAndPopup();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  private startRealTimeRefresh(): void {
    if (this.realTimeRefreshId) {
      clearInterval(this.realTimeRefreshId);
    }

    this.realTimeRefreshId = setInterval(() => {
      this.loadBookings(false);
      this.loadVaccinations(false);
      this.loadOrders(false);
    }, 15000);
  }

  private markCounterSeen(
    day: MonthCalendarCell,
    type: 'bookings' | 'vaccinations'
  ): void {
    this.seenCounterKeys.add(this.getCounterKey(day, type));
    this.saveSeenCounterKeys();
    this.updateBookingAlertSound();
  }

  private markCurrentMonthBookingsSeen(): void {
    this.markBookingsSeen(this.getCurrentMonthBookings());
  }

  private markBookingsSeen(bookings: ClinicCalendarBooking[]): void {
    bookings.forEach((booking) => {
      this.seenCounterKeys.add(this.getBookingAlertKey(booking));
    });

    this.saveSeenCounterKeys();
    this.stopBookingAlertSound();
  }

  private getCounterKey(
    day: MonthCalendarCell,
    type: 'bookings' | 'vaccinations'
  ): string {
    if (type === 'bookings') {
      return `${type}-${this.formatDateKey(day.date)}-${day.bookings.length}`;
    }

    return `${type}-${this.formatDateKey(day.date)}`;
  }

  private getBookingAlertKey(booking: ClinicCalendarBooking): string {
    const bookingId = Number(booking.id || 0);

    if (bookingId > 0) {
      return `booking-${bookingId}`;
    }

    return `booking-${this.formatDateKey(booking.date)}-${booking.clientName}-${booking.serviceName}-${booking.date.getTime()}`;
  }

  private loadSeenCounterKeys(): void {
    const savedKeys = this.readFromStorage(this.seenCounterStorageKey);
    if (!savedKeys) return;

    try {
      const parsedKeys = JSON.parse(savedKeys);
      if (!Array.isArray(parsedKeys)) return;

      parsedKeys
        .filter((key): key is string => typeof key === 'string' && key.length > 0)
        .forEach((key) => this.seenCounterKeys.add(key));
    } catch {
      localStorage.removeItem(this.seenCounterStorageKey);
    }
  }

  private saveSeenCounterKeys(): void {
    if (typeof window === 'undefined') return;

    const keys = Array.from(this.seenCounterKeys).slice(-500);
    localStorage.setItem(this.seenCounterStorageKey, JSON.stringify(keys));
  }

  private updateBookingAlertSound(): void {
    const unplayedAlertKeys = this.getUnplayedBookingAlertKeys();

    if (unplayedAlertKeys.length > 0) {
      this.startBookingAlertSound(unplayedAlertKeys);
      return;
    }
  }

  private getUnplayedBookingAlertKeys(): string[] {
    return this.getCurrentMonthBookings()
      .map((booking) => this.getBookingAlertKey(booking))
      .filter((key) => {
        return !this.seenCounterKeys.has(key) && !this.playedBookingAlertKeys.has(key);
      });
  }

  private startBookingAlertSound(alertKeys: string[]): void {
    if (typeof window === 'undefined' || alertKeys.length === 0) return;

    const alertKeySet = new Set(alertKeys);
    const hasCurrentUnseenBooking = this.getCurrentMonthBookings().some((item) => {
      const key = this.getBookingAlertKey(item);
      return alertKeySet.has(key) && !this.seenCounterKeys.has(key);
    });

    if (!hasCurrentUnseenBooking) return;

    if (!this.bookingAlertAudio) {
      this.bookingAlertAudio = new Audio('/assets/sound/order.mp3');
      this.bookingAlertAudio.loop = false;
      this.bookingAlertAudio.preload = 'auto';
    }

    this.bookingAlertAudio.currentTime = 0;
    this.bookingAlertAudio.play().then(() => {
      alertKeys.forEach((key) => this.playedBookingAlertKeys.add(key));
    }).catch(() => {
      // Browsers may block audio until the first user interaction.
    });
  }

  private stopBookingAlertSound(): void {
    if (!this.bookingAlertAudio) return;

    this.bookingAlertAudio.pause();
    this.bookingAlertAudio.currentTime = 0;
  }

  private buildMonthCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const startOffset = firstDayOfMonth.getDay();
    const gridStartDate = this.addDays(firstDayOfMonth, -startOffset);
    const cells: MonthCalendarCell[] = [];

    for (let i = 0; i < 42; i++) {
      const date = this.addDays(gridStartDate, i);
      const openingDay = this.getOpeningDayFromDate(date);
      const daySchedule = this.getScheduleByDay(openingDay);
      const shifts = this.getCalendarShiftsForDate(date, daySchedule);

      cells.push({
        date,
        dayNumber: date.getDate(),
        dayLabel: this.getDayLabel(openingDay),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isToday(date),
        is_open: daySchedule.is_open || shifts.length > 0,
        shifts,
        bookings: this.getBookingsForDate(date),
        vaccinations: this.getVaccinationsForDate(date),
        orders: this.getOrdersForDate(date),
        ordersCount: this.getOrdersCountForDate(date),
        ordersTotalMoney: this.getOrdersMoneyForDate(date),
      });
    }

    this.calendarDays = cells;
    this.updateBookingAlertSound();
  }

  private getScheduleByDay(day: OpeningDay): DaySchedule {
    return (
      this.schedule.find((item) => item.day === day) || {
        day,
        labelAr: this.getDayLabel(day),
        is_open: false,
        shifts: [],
      }
    );
  }

  private getCalendarShiftsForDate(
    date: Date,
    daySchedule: DaySchedule
  ): ClinicCalendarShift[] {
    const baseShifts = daySchedule.shifts.map((shift) => ({
      ...shift,
      user_ids: this.normalizeUserIds(shift.user_ids),
      users: Array.isArray(shift.users) ? [...shift.users] : undefined,
      source: 'base' as const,
    }));

    const emergencyShifts =
      this.emergencyShiftsByDate.get(this.formatDateKey(date)) || [];

    return this.mergeCalendarShifts(baseShifts, emergencyShifts);
  }

  private mergeCalendarShifts(
    baseShifts: ClinicCalendarShift[],
    emergencyShifts: ClinicCalendarShift[]
  ): ClinicCalendarShift[] {
    const merged = baseShifts.map((shift) => ({ ...shift }));

    emergencyShifts.forEach((emergencyShift) => {
      const existing = merged.find(
        (shift) => shift.start === emergencyShift.start && shift.end === emergencyShift.end
      );

      if (!existing) {
        merged.push({ ...emergencyShift, source: 'emergency' });
        return;
      }

      const baseUsers = this.getShiftUsers(existing);
      const emergencyUsers = this.getShiftUsers(emergencyShift);

      existing.disabled_users = this.uniqueUsers(baseUsers);
      existing.disabled_user_ids = this.normalizeUserIds(existing.user_ids || []);
      existing.user_ids = this.normalizeUserIds(emergencyShift.user_ids || []);
      existing.users = this.uniqueUsers(emergencyUsers);
      existing.source = 'mixed';
    });

    return merged.sort((a, b) => this.toMinutes(a.start) - this.toMinutes(b.start));
  }

  private getOpeningDayFromDate(date: Date): OpeningDay {
    return this.dayOrder[date.getDay()];
  }

  private isToday(date: Date): boolean {
    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  }

  private getBookingsForDate(date: Date): ClinicCalendarBooking[] {
    return this.bookings.filter((booking) => this.isSameDate(booking.date, date));
  }

  private getVaccinationsForDate(date: Date): ClinicCalendarVaccination[] {
    return this.vaccinations.filter((vaccination) =>
      this.isSameDate(vaccination.date, date)
    );
  }

  private getOrdersCountForDate(date: Date): number {
    return this.ordersTotalsByDay.get(this.formatDateKey(date)) || 0;
  }

  private getOrdersForDate(date: Date): ClinicCalendarOrder[] {
    return this.ordersByDay.get(this.formatDateKey(date)) || [];
  }

  private getOrdersMoneyForDate(date: Date): number {
    return this.ordersMoneyByDay.get(this.formatDateKey(date)) || 0;
  }

  private updateStoredBooking(booking: ClinicCalendarBooking): void {
    this.bookings = this.bookings.map((item) =>
      item.id === booking.id ? { ...booking } : item
    );
    this.rebuildCalendarAndPopup();
  }

  private rebuildCalendarAndPopup(): void {
    const selectedDate = this.selectedDay?.date;
    this.buildMonthCalendar();

    if (selectedDate) {
      this.selectedDay =
        this.calendarDays.find((day) => this.isSameDate(day.date, selectedDate)) ||
        null;
    }
  }

  private isSameDate(first: Date, second: Date): boolean {
    return (
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate()
    );
  }

  private isCurrentMonthDate(date: Date): boolean {
    return (
      date.getFullYear() === this.currentMonth.getFullYear() &&
      date.getMonth() === this.currentMonth.getMonth()
    );
  }

  private formatDateKey(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private getShiftDateRange(date: Date, shift: ClinicCalendarShift): { start: Date; end: Date } | null {
    const startMinutes = this.toMinutes(shift.start);
    const endMinutes = this.toMinutes(shift.end);

    if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) return null;

    const start = new Date(date);
    start.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0);

    const end = new Date(date);
    end.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);

    if (end <= start) {
      end.setDate(end.getDate() + 1);
    }

    return { start, end };
  }

  private isDateInRange(date: Date, start: Date, end: Date): boolean {
    return date >= start && date < end;
  }

  private normalizeBooking(booking: any): ClinicCalendarBooking | null {
    const date = this.parseBookingDate(
      Number(booking?.status) === 3 && booking?.deferred_booking_date
        ? booking.deferred_booking_date
        : booking?.booking_date
    );

    if (!date) return null;

    return {
      id: Number(booking?.id || 0),
      clientName: booking?.client_name || booking?.user?.name || this.labels.clientFallback,
      serviceName:
        booking?.pet_service?.name_ar ||
        booking?.petService?.name_ar ||
        booking?.pet_service?.name_en ||
        booking?.petService?.name_en ||
        this.labels.bookingFallback,
      phone: booking?.phone_number || '',
      status: Number(booking?.status || 0),
      date,
    };
  }

  private normalizeVaccination(vaccination: any): ClinicCalendarVaccination | null {
    const date = this.parseBookingDate(
      vaccination?.next_due_date || vaccination?.vaccination_date
    );

    if (!date) return null;

    return {
      id: Number(vaccination?.id || 0),
      petId: this.extractVaccinationPetId(vaccination),
      vaccineName: vaccination?.vaccine_name || this.labels.vaccinationFallback,
      petName:
        vaccination?.pet?.name ||
        vaccination?.pet_name ||
        vaccination?.pet?.pet_name ||
        this.labels.petFallback,
      date,
    };
  }

  private extractVaccinationPetId(vaccination: any): number | null {
    const rawPetId =
      vaccination?.pet_id ??
      vaccination?.petId ??
      vaccination?.pet?.id ??
      vaccination?.animal_id ??
      vaccination?.animalId;
    const petId = Number(rawPetId);

    return petId > 0 ? petId : null;
  }

  private normalizeOrdersTotalsByDate(response: any): Map<string, number> {
    const totals = new Map<string, number>();
    const rows = this.extractOrderTotalRows(response);

    rows.forEach((row: any) => {
      const orderDate = this.extractOrderDate(row);
      const day = this.extractOrderTotalDay(row, orderDate);
      const count = this.extractOrderTotalCount(row, orderDate);

      if (!day || count <= 0) return;

      const date =
        orderDate ||
        new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);

      if (
        date.getFullYear() !== this.currentMonth.getFullYear() ||
        date.getMonth() !== this.currentMonth.getMonth()
      ) {
        return;
      }

      const key = this.formatDateKey(date);
      totals.set(key, (totals.get(key) || 0) + count);
    });

    return totals;
  }

  private loadOrdersFromWarehouseList(
    warehouseId: number,
    page = 1,
    collectedOrders: any[] = [],
    showLoading = true
  ): void {
    this.ordersService
      .getOrdersByWarehouseId(warehouseId, page, 500)
      .pipe(this.maybeTrackCalendarLoading(showLoading), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const rows = this.extractOrdersRows(res);
          const allOrders = [...collectedOrders, ...rows];
          const lastPage = this.extractLastPage(res, page);

          if (page < lastPage && page < 12) {
            this.loadOrdersFromWarehouseList(warehouseId, page + 1, allOrders, showLoading);
            return;
          }

          this.setOrdersFromRows(allOrders);
          this.rebuildCalendarAndPopup();
        },
        error: (err) => {
          console.error(err);
          this.setOrdersFromRows(collectedOrders);
          this.rebuildCalendarAndPopup();
        },
      });
  }

  private setOrdersFromRows(orders: any[]): void {
    const normalizedOrders = orders
      .map((order) => this.normalizeOrder(order))
      .filter((order: ClinicCalendarOrder | null): order is ClinicCalendarOrder => !!order);

    this.ordersTotalsByDay = this.aggregateOrdersByDate(normalizedOrders);
    this.ordersMoneyByDay = this.aggregateOrdersMoneyByDate(normalizedOrders);
    this.ordersByDay = this.groupOrdersByDate(normalizedOrders);
  }

  private aggregateOrdersByDate(orders: ClinicCalendarOrder[]): Map<string, number> {
    const totals = new Map<string, number>();

    orders.forEach((order) => {
      const date = order.date;

      if (
        !date ||
        date.getFullYear() !== this.currentMonth.getFullYear() ||
        date.getMonth() !== this.currentMonth.getMonth()
      ) {
        return;
      }

      const key = this.formatDateKey(date);
      totals.set(key, (totals.get(key) || 0) + 1);
    });

    return totals;
  }

  private aggregateOrdersMoneyByDate(orders: ClinicCalendarOrder[]): Map<string, number> {
    const totals = new Map<string, number>();

    orders.forEach((order) => {
      const date = order.date;

      if (
        !date ||
        date.getFullYear() !== this.currentMonth.getFullYear() ||
        date.getMonth() !== this.currentMonth.getMonth()
      ) {
        return;
      }

      const key = this.formatDateKey(date);
      totals.set(key, (totals.get(key) || 0) + order.total);
    });

    return totals;
  }

  private groupOrdersByDate(orders: ClinicCalendarOrder[]): Map<string, ClinicCalendarOrder[]> {
    const grouped = new Map<string, ClinicCalendarOrder[]>();

    orders.forEach((order) => {
      const date = order.date;

      if (
        !date ||
        date.getFullYear() !== this.currentMonth.getFullYear() ||
        date.getMonth() !== this.currentMonth.getMonth()
      ) {
        return;
      }

      const key = this.formatDateKey(date);
      grouped.set(key, [...(grouped.get(key) || []), order]);
    });

    grouped.forEach((dayOrders, key) => {
      grouped.set(
        key,
        dayOrders.sort((a, b) => b.date.getTime() - a.date.getTime())
      );
    });

    return grouped;
  }

  private extractOrdersRows(response: any): any[] {
    const candidates = [
      response,
      response?.data,
      response?.data?.data,
      response?.orders,
      response?.data?.orders,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    return [];
  }

  private extractLastPage(response: any, fallback: number): number {
    return Number(
      response?.last_page ??
        response?.data?.last_page ??
        response?.meta?.last_page ??
        response?.data?.meta?.last_page ??
        fallback
    );
  }

  private extractOrderTotalRows(response: any): any[] {
    const candidates = [
      response,
      response?.data,
      response?.data?.data,
      response?.totals,
      response?.data?.totals,
      response?.orders,
      response?.data?.orders,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    const objectCandidate = candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === 'object' &&
        !Array.isArray(candidate) &&
        Object.keys(candidate).some((key) => /^\d{1,2}$/.test(key))
    );

    if (!objectCandidate) return [];

    return Object.entries(objectCandidate)
      .filter(([key, value]) => /^\d{1,2}$/.test(key) && Number(value || 0) > 0)
      .map(([day, count]) => ({ day, count }));
  }

  private extractOrderTotalDay(row: any, parsedDate?: Date | null): number {
    const directDay =
      row?.day ??
      row?.order_day ??
      row?.date_day ??
      row?.day_number;

    if (directDay !== null && directDay !== undefined && directDay !== '') {
      return Number(directDay);
    }

    const date = parsedDate || this.extractOrderDate(row);

    return date ? date.getDate() : 0;
  }

  private extractOrderTotalCount(row: any, parsedDate?: Date | null): number {
    const total = Number(
      row?.count ??
        row?.orders_count ??
        row?.total_orders ??
        row?.orders_total ??
        row?.total ??
        row?.value ??
        0
    );

    if (total > 0) return total;

    return parsedDate || this.extractOrderDate(row) ? 1 : 0;
  }

  private normalizeOrder(order: any): ClinicCalendarOrder | null {
    const date = this.extractOrderDate(order);

    if (!date) return null;

    return {
      id: Number(order?.id || 0),
      clientName:
        order?.client_name ||
        order?.client?.name ||
        order?.user?.name ||
        this.labels.clientFallback,
      phone: order?.phone_number || order?.phone || order?.client?.phone || '',
      total: this.extractOrderMoney(order),
      status: order?.order_status ?? order?.status ?? '',
      date,
      raw: order,
    };
  }

  private normalizeFinancialRecord(record: any): ClinicFinancialRecord | null {
    const date = this.extractFinancialRecordDate(record);
    if (!date) return null;

    return {
      amount: this.toNumber(record?.amount ?? record?.total ?? record?.value),
      date,
    };
  }

  private parseArrayField(value: any): any[] {
    if (value === null || value === undefined || value === '') return [];
    if (Array.isArray(value)) return value;

    if (typeof value === 'object') {
      return Object.values(value);
    }

    const textValue = String(value).trim();
    if (!textValue) return [];

    try {
      const parsed = JSON.parse(textValue);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return Object.values(parsed);
      if (parsed !== null && parsed !== undefined && parsed !== '') return [parsed];
    } catch {
      // Some older order responses store arrays as comma-separated strings.
    }

    if (textValue.includes(',')) {
      return textValue.split(',').map((item) => item.trim()).filter(Boolean);
    }

    return [textValue];
  }

  private extractItemName(item: any): string {
    if (!item) return '';
    if (typeof item !== 'object') return String(item);

    return String(
      item.name ||
        item.product_name ||
        item.item_name ||
        item.title ||
        item.stock_name ||
        item.stock?.name ||
        item.product?.name ||
        ''
    );
  }

  private extractItemNumber(item: any, keys: string[]): number {
    if (!item || typeof item !== 'object') return 0;

    for (const key of keys) {
      const value = this.toNumber(item[key]);
      if (value) return value;
    }

    return 0;
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') return 0;
    return Number(String(value).replace(/,/g, '')) || 0;
  }

  private extractOrderMoney(order: any): number {
    const rawTotal =
      order?.total ??
      order?.total_money ??
      order?.total_amount ??
      order?.grand_total ??
      0;

    return Number(String(rawTotal).replace(/,/g, '') || 0);
  }

  private extractOrderDate(order: any): Date | null {
    return this.parseBookingDate(
      order?.created_at ||
        order?.order_date ||
        order?.date ||
        order?.day_date ||
        order?.createdAt ||
        order?.updated_at
    );
  }

  private extractFinancialRecordDate(record: any): Date | null {
    return this.parseBookingDate(
      record?.created_at ||
        record?.entry_date ||
        record?.date ||
        record?.day_date ||
        record?.createdAt ||
        record?.updated_at
    );
  }

  private extractArrayResponse(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.vaccinations)) return response.vaccinations;
    if (Array.isArray(response?.data?.vaccinations)) return response.data.vaccinations;

    return [];
  }

  private parseBookingDate(value: any): Date | null {
    if (!value) return null;

    const cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim();

    const mysqlMatch = cleanValue.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/
    );

    if (mysqlMatch) {
      const year = Number(mysqlMatch[1]);
      const month = Number(mysqlMatch[2]) - 1;
      const day = Number(mysqlMatch[3]);
      const hours = Number(mysqlMatch[4] || 0);
      const minutes = Number(mysqlMatch[5] || 0);
      const seconds = Number(mysqlMatch[6] || 0);

      return new Date(year, month, day, hours, minutes, seconds);
    }

    const parsed = new Date(cleanValue);

    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDateTimeForInput(dateValue: Date): string {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    const hours = String(dateValue.getHours()).padStart(2, '0');
    const minutes = String(dateValue.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private convertDateTimeLocalToMysql(dateTimeLocal: string): string {
    const normalized = dateTimeLocal.replace('T', ' ');

    return normalized.length === 16 ? `${normalized}:00` : normalized;
  }

  private resolveWarehouseId(): string | number | null {
    return (
      this.route.snapshot.paramMap.get('warehouseId') ||
      this.route.snapshot.paramMap.get('id') ||
      this.readFromStorage('posId') ||
      this.readFromStorage('selectedWarehouseId') ||
      this.readFromStorage('selectedBusinessId')
    );
  }

  private readFromStorage(key: string): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  }

  private getEmptySchedule(): DaySchedule[] {
    return this.dayOrder.map((day) => ({
      day,
      labelAr: this.getDayLabel(day),
      is_open: false,
      shifts: [],
    }));
  }

  private normalizeOpeningHours(response: any): DaySchedule[] {
    const map = new Map<OpeningDay, DaySchedule>();

    this.getEmptySchedule().forEach((day) => {
      map.set(day.day, day);
    });

    const rows = this.extractOpeningRows(response);

    rows.forEach((row: any) => {
      const day = row?.day as OpeningDay;

      if (!this.dayOrder.includes(day)) return;

      const current = map.get(day);
      if (!current) return;

      const isOpen =
        row?.is_open === true ||
        row?.is_open === 1 ||
        row?.is_open === '1' ||
        row?.is_open === 'true';

      current.is_open = current.is_open || isOpen;

      if (Array.isArray(row?.shifts)) {
        row.shifts.forEach((shift: any) => {
          const normalized = this.normalizeShift(shift);
          if (normalized) current.shifts.push(normalized);
        });
      } else {
        const normalized = this.normalizeShift({
          start: row?.opens_at,
          end: row?.closes_at,
        });

        if (normalized) current.shifts.push(normalized);
      }
    });

    return Array.from(map.values()).map((day) => ({
      ...day,
      shifts: day.shifts.sort(
        (a, b) => this.toMinutes(a.start) - this.toMinutes(b.start)
      ),
    }));
  }

  private getCurrentMonthDateKeys(): string[] {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, index) =>
      this.formatDateKey(new Date(year, month, index + 1))
    );
  }

  private extractEffectiveOpeningHour(res: any, dateKey: string): any {
    const data = res?.data || res?.effective || res?.opening_hour || res || {};
    const nested = data?.opening_hour || data?.schedule || data;

    return {
      ...nested,
      day: nested?.day || data?.day || this.getOpeningDayFromDate(this.parseDateOnly(dateKey)),
      is_open: nested?.is_open ?? data?.is_open ?? true,
      is_24_hours:
        nested?.is_24_hours ??
        nested?.is24hours ??
        data?.is_24_hours ??
        data?.is24hours ??
        false,
      shifts: nested?.shifts ?? data?.shifts ?? [],
    };
  }

  private extractEffectiveSource(res: any): 'default' | 'override' {
    const source = String(
      res?.source || res?.data?.source || res?.effective?.source || ''
    ).toLowerCase();

    return source === 'override' ? 'override' : 'default';
  }

  private extractCalendarShiftsFromRow(row: any): ClinicCalendarShift[] {
    const shifts = Array.isArray(row?.shifts) ? row.shifts : [];

    if (shifts.length > 0) {
      return shifts
        .map((shift: any) => this.normalizeShift(shift))
        .filter((shift: ClinicCalendarShift | null): shift is ClinicCalendarShift => !!shift);
    }

    const normalized = this.normalizeShift({
      start: row?.opens_at,
      end: row?.closes_at,
      users: row?.users,
      user_ids: row?.user_ids,
    });

    return normalized ? [normalized] : [];
  }

  private extractOpeningRows(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.opening_hours)) return response.data.opening_hours;
    if (Array.isArray(response?.data?.openingHours)) return response.data.openingHours;
    if (Array.isArray(response?.opening_hours)) return response.opening_hours;
    if (Array.isArray(response?.openingHours)) return response.openingHours;

    return [];
  }

  private normalizeShift(shift: any): ClinicCalendarShift | null {
    const start = this.normalizeTime(shift?.start || shift?.opens_at);
    const end = this.normalizeTime(shift?.end || shift?.closes_at);

    if (!start || !end) return null;

    const users = Array.isArray(shift?.users) ? shift.users : [];
    users.forEach((user: ClinicShiftUser) => this.registerUser(user));

    const userIds = this.normalizeUserIds(
      shift?.user_ids || shift?.userIds || users.map((user: ClinicShiftUser) => user?.id)
    );

    return { start, end, user_ids: userIds, users };
  }

  private getShiftUsers(shift: ClinicCalendarShift): ClinicShiftUser[] {
    const directUsers = Array.isArray(shift.users) ? shift.users : [];
    directUsers.forEach((user) => this.registerUser(user));

    const usersByIds = this.normalizeUserIds(shift.user_ids)
      .map((id) => this.usersMap.get(id))
      .filter(Boolean) as ClinicShiftUser[];

    const usersMap = new Map<number, ClinicShiftUser>();

    [...directUsers, ...usersByIds].forEach((user) => {
      const id = Number(user?.id);
      if (!Number.isNaN(id)) usersMap.set(id, user);
    });

    return Array.from(usersMap.values());
  }

  private getDisabledShiftUsers(shift: ClinicCalendarShift): ClinicShiftUser[] {
    const directUsers = Array.isArray(shift.disabled_users) ? shift.disabled_users : [];
    directUsers.forEach((user) => this.registerUser(user));

    const usersByIds = this.normalizeUserIds(shift.disabled_user_ids)
      .map((id) => this.usersMap.get(id))
      .filter(Boolean) as ClinicShiftUser[];

    return this.uniqueUsers([...directUsers, ...usersByIds]);
  }

  private getShiftUserActionKey(
    shift: ClinicCalendarShift,
    user: ClinicShiftUser
  ): string {
    return `${shift.start}-${shift.end}-${user.id}`;
  }

  private registerUser(user: ClinicShiftUser): void {
    const id = Number(user?.id);
    if (!Number.isNaN(id)) {
      this.usersMap.set(id, user);
    }
  }

  private getUserName(user: ClinicShiftUser): string {
    const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

    return (
      user?.name ||
      user?.name_en ||
      fullName ||
      user?.email ||
      user?.phone ||
      ''
    );
  }

  private normalizeUserIds(value: any): number[] {
    if (!Array.isArray(value)) return [];

    return Array.from(
      new Set(
        value
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      )
    );
  }

  private uniqueUsers(users: ClinicShiftUser[]): ClinicShiftUser[] {
    const map = new Map<number, ClinicShiftUser>();

    users.forEach((user) => {
      const id = Number(user?.id);
      if (!Number.isNaN(id)) {
        this.registerUser(user);
        map.set(id, user);
      }
    });

    return Array.from(map.values());
  }

  private parseDateOnly(dateKey: string): Date {
    const [year, month, day] = dateKey.split('-').map((part) => Number(part));
    return new Date(year, month - 1, day);
  }

  private extractEmployeesResponse(response: any): ClinicShiftUser[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.employees)) return response.employees;
    if (Array.isArray(response?.data?.employees)) return response.data.employees;

    return [];
  }

  private extractClinicName(response: any): string {
    const clinic = response?.data || response?.warehouse || response;
    const arabicName = this.firstTextValue([
      clinic?.name_ar,
      clinic?.name,
      clinic?.title_ar,
      clinic?.title,
    ]);
    const englishName = this.firstTextValue([
      clinic?.name_en,
      clinic?.english_name,
      clinic?.title_en,
      clinic?.name,
      clinic?.name_ar,
    ]);
    const frenchName = this.firstTextValue([
      clinic?.name_fr,
      clinic?.french_name,
      clinic?.title_fr,
      clinic?.name_en,
      clinic?.name,
    ]);

    if (this.isFr) return frenchName || englishName || arabicName;
    return this.isAr ? arabicName || englishName : englishName || arabicName;
  }

  private extractClinicLocation(response: any): string {
    const clinic = response?.data || response?.warehouse || response;

    return this.firstTextValue([
      clinic?.location,
      clinic?.address,
      clinic?.area,
      clinic?.city,
      clinic?.governorate,
      clinic?.warehouse_location,
      clinic?.branch_location,
    ]);
  }

  private extractWarehouseTotalValue(response: any): number {
    const clinic = response?.data || response?.warehouse || response;
    const totalValue =
      clinic?.total_value ??
      clinic?.totalValue ??
      clinic?.total_business ??
      clinic?.totalBusiness ??
      clinic?.stock_total_value ??
      clinic?.stockTotalValue ??
      0;

    return Number(totalValue || 0);
  }

  private loadWarehouseTotalValueFromList(): void {
    if (!this.warehouseId) return;

    this.posService
      .getAllPOS(1)
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const warehouse = this.findCurrentWarehouse(this.extractWarehouseRows(res));
          const totalValue = this.extractWarehouseTotalValue(warehouse);

          if (totalValue > 0) {
            this.warehouseTotalValue = totalValue;
            return;
          }

          this.loadUserWarehouseTotalValue();
        },
        error: () => this.loadUserWarehouseTotalValue(),
      });
  }

  private loadUserWarehouseTotalValue(): void {
    this.userService
      .getUserId()
      .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const userId = res?.user_id || localStorage.getItem('user_id');
          if (!userId) return;

          this.posService
            .getUserPOS(String(userId))
            .pipe(this.trackCalendarLoading(), takeUntil(this.destroy$))
            .subscribe({
              next: (warehousesRes: any) => {
                const warehouse = this.findCurrentWarehouse(
                  this.extractWarehouseRows(warehousesRes)
                );
                this.warehouseTotalValue = this.extractWarehouseTotalValue(warehouse);
              },
            });
        },
      });
  }

  private extractWarehouseRows(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.warehouse)) return response.warehouse;
    if (Array.isArray(response?.warehouses)) return response.warehouses;

    return [];
  }

  private findCurrentWarehouse(rows: any[]): any {
    return rows.find((warehouse) => String(warehouse?.id) === String(this.warehouseId));
  }

  private firstTextValue(values: any[]): string {
    return (
      values
        .map((value) => String(value || '').trim())
        .find((value) => value.length > 0) || ''
    );
  }

  private normalizeTime(value: any): string | null {
    if (typeof value !== 'string') return null;

    const match = value.trim().match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;

    const hour = Math.min(Math.max(Number(match[1]), 0), 23);
    const minute = Math.min(Math.max(Number(match[2]), 0), 59);

    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  private formatTime(time: string): string {
    const totalMinutes = this.toMinutes(time);
    const hour24 = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const period = this.isAr
      ? hour24 < 12
        ? '\u0635'
        : '\u0645'
      : hour24 < 12
        ? 'AM'
        : 'PM';
    const hour12 = hour24 % 12 || 12;

    return `${this.formatNumber(String(hour12).padStart(2, '0'))}:${this.formatNumber(String(minute).padStart(2, '0'))} ${period}`;
  }

  private toMinutes(time: string): number {
    const [hour, minute] = time.split(':').map(Number);
    return hour * 60 + minute;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private applyLanguage(language: string): void {
    this.currentLanguage = ['ar', 'en', 'fr'].includes(language) ? language : 'en';
    this.isAr = this.currentLanguage === 'ar';
    this.isFr = this.currentLanguage === 'fr';
    this.labels = this.getLabels();
  }

  private getLocale(): string {
    if (this.isAr) return 'ar-EG';
    if (this.isFr) return 'fr-FR';

    return 'en-US';
  }

  private getDayLabel(day: OpeningDay): string {
    if (this.isAr) return this.dayLabelsAr[day];
    if (this.isFr) return this.dayLabelsFr[day];

    return this.dayLabelsEn[day];
  }

  private getLabels() {
    if (this.isAr) {
      return {
        calendar: 'Calendar',
        totalGoods: '\u0625\u062c\u0645\u0627\u0644\u064a \u0633\u0639\u0631 \u0627\u0644\u0628\u0636\u0627\u0626\u0639',
        currency: '\u062c\u0646\u064a\u0647',
        title: '\u062a\u0642\u0648\u064a\u0645 \u0645\u0648\u0627\u0639\u064a\u062f \u0627\u0644\u0639\u064a\u0627\u062f\u0629',
        subtitle: '\u0639\u0631\u0636 \u0634\u0647\u0631\u064a \u0644\u0645\u0648\u0627\u0639\u064a\u062f \u0641\u062a\u062d \u0627\u0644\u0639\u064a\u0627\u062f\u0629 \u062d\u0633\u0628 \u0627\u0644\u062c\u062f\u0648\u0644 \u0627\u0644\u0645\u0633\u062c\u0644.',
        newOrder: '\u0641\u0627\u062a\u0648\u0631\u0629 \u062c\u062f\u064a\u062f\u0629',
        supply: '\u062a\u063a\u064a\u064a\u0631 \u0627\u0636\u0637\u0631\u0627\u0631\u064a \u0644\u0644\u0634\u0641\u062a',
        accounts: '\u0627\u0644\u062d\u0633\u0627\u0628\u0627\u062a',
        newBooking: '\u062d\u062c\u0632 \u062c\u062f\u064a\u062f',
        refresh: '\u062a\u062d\u062f\u064a\u062b',
        monthHint: '\u0643\u0644 \u064a\u0648\u0645 \u0628\u064a\u0639\u0631\u0636 \u0645\u0648\u0627\u0639\u064a\u062f \u0627\u0644\u0634\u063a\u0644 \u0627\u0644\u062e\u0627\u0635\u0629 \u0628\u064a\u0647',
        availableHours: '\u0645\u0648\u0627\u0639\u064a\u062f \u0645\u062a\u0627\u062d\u0629',
        loading: '\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0645\u0648\u0627\u0639\u064a\u062f \u0627\u0644\u0639\u064a\u0627\u062f\u0629...',
        today: '\u0627\u0644\u064a\u0648\u0645',
        closed: '\u0645\u063a\u0644\u0642',
        previousMonth: '\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u0633\u0627\u0628\u0642',
        currentMonth: '\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u062d\u0627\u0644\u064a',
        nextMonth: '\u0627\u0644\u0634\u0647\u0631 \u0627\u0644\u062a\u0627\u0644\u064a',
        bookings: '\u062d\u062c\u0648\u0632\u0627\u062a \u0627\u0644\u0639\u0645\u0644\u0627\u0621',
        orders: '\u0627\u0644\u0623\u0648\u0631\u062f\u0631\u0627\u062a',
        totalInvoicesMoney: '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u0628\u064a\u0639\u0627\u062a',
        invoiceNumber: '\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        invoiceAmount: '\u0642\u064a\u0645\u0629 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        orderTime: '\u0648\u0642\u062a \u0627\u0644\u0623\u0648\u0631\u062f\u0631',
        invoice: '\u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        invoiceDate: '\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        customer: '\u0627\u0644\u0639\u0645\u064a\u0644',
        phoneLabel: '\u0627\u0644\u0645\u0648\u0628\u0627\u064a\u0644',
        invoiceItems: '\u0628\u0646\u0648\u062f \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        product: '\u0627\u0644\u0645\u0646\u062a\u062c',
        service: '\u0627\u0644\u062e\u062f\u0645\u0629',
        productOrServiceSearchPlaceholder: '\u0627\u0633\u0645 \u0627\u0644\u0645\u0646\u062a\u062c \u0623\u0648 \u0627\u0644\u062e\u062f\u0645\u0629',
        productFallback: '\u0645\u0646\u062a\u062c',
        quantityLabel: '\u0627\u0644\u0643\u0645\u064a\u0629',
        priceLabel: '\u0627\u0644\u0633\u0639\u0631',
        lineTotalLabel: '\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a',
        subtotalLabel: '\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a \u0642\u0628\u0644 \u0627\u0644\u062e\u0635\u0645',
        discountLabel: '\u0627\u0644\u062e\u0635\u0645',
        deliveryFeesLabel: '\u0645\u0635\u0627\u0631\u064a\u0641 \u0627\u0644\u062a\u0648\u0635\u064a\u0644',
        paidLabel: '\u0627\u0644\u0645\u062f\u0641\u0648\u0639',
        remainingLabel: '\u0627\u0644\u0645\u062a\u0628\u0642\u064a',
        noInvoiceItems: '\u0645\u0641\u064a\u0634 \u0628\u0646\u0648\u062f \u0645\u0641\u0635\u0644\u0629 \u0644\u0644\u0641\u0627\u062a\u0648\u0631\u0629',
        vaccinations: '\u0627\u0644\u062a\u0637\u0639\u064a\u0645\u0627\u062a',
        shifts: '\u0627\u0644\u0634\u0641\u062a\u0627\u062a',
        shiftTime: '\u0645\u064a\u0639\u0627\u062f \u0627\u0644\u0634\u0641\u062a',
        shiftEmployees: '\u0627\u0644\u0646\u0627\u0633 \u0627\u0644\u0644\u064a \u0634\u063a\u0627\u0644\u0629',
        employeesCount: '\u0645\u0648\u0638\u0641',
        shiftSales: '\u0645\u0628\u064a\u0639\u0627\u062a',
        shiftExpenses: '\u0645\u0635\u0631\u0648\u0641\u0627\u062a',
        shiftProfit: '\u0635\u0627\u0641\u064a \u0627\u0644\u0631\u0628\u062d',
        noShiftEmployees: '\u0645\u0641\u064a\u0634 \u0645\u0648\u0638\u0641\u064a\u0646 \u0645\u062a\u0633\u062c\u0644\u064a\u0646 \u0639\u0644\u0649 \u0627\u0644\u0634\u0641\u062a',
        employeeDetails: '\u062a\u0641\u0627\u0635\u064a\u0644 \u0627\u0644\u0645\u0648\u0638\u0641',
        employeeSales: '\u0645\u0628\u064a\u0639\u0627\u062a \u0627\u0644\u0645\u0648\u0638\u0641',
        pending: '\u0642\u064a\u062f \u0627\u0644\u0627\u0646\u062a\u0638\u0627\u0631',
        approved: '\u0645\u0642\u0628\u0648\u0644',
        completed: '\u0645\u0643\u062a\u0645\u0644',
        deferred: '\u062a\u0623\u062c\u064a\u0644',
        changeDeferredDate: '\u062a\u063a\u064a\u064a\u0631 \u062a\u0627\u0631\u064a\u062e \u0627\u0644\u062a\u0623\u062c\u064a\u0644',
        rejected: '\u0645\u0631\u0641\u0648\u0636',
        delete: '\u062d\u0630\u0641',
        close: '\u0625\u063a\u0644\u0627\u0642',
        submit: '\u062d\u0641\u0638',
        loadingAction: '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...',
        deferredDate: '\u062a\u0627\u0631\u064a\u062e \u0627\u0644\u062a\u0623\u062c\u064a\u0644',
        noClinicId: '\u0645\u0634 \u0644\u0627\u0642\u064a ID \u0627\u0644\u0639\u064a\u0627\u062f\u0629 / \u0627\u0644\u0645\u062e\u0632\u0646.',
        openingHoursError: '\u062d\u0635\u0644 \u062e\u0637\u0623 \u0623\u062b\u0646\u0627\u0621 \u062a\u062d\u0645\u064a\u0644 \u0645\u0648\u0627\u0639\u064a\u062f \u0627\u0644\u0639\u064a\u0627\u062f\u0629.',
        updateStatusError: '\u062a\u0639\u0630\u0631 \u062a\u062d\u062f\u064a\u062b \u062d\u0627\u0644\u0629 \u0627\u0644\u062d\u062c\u0632',
        updateDeferredError: '\u062a\u0639\u0630\u0631 \u062a\u062d\u062f\u064a\u062b \u062a\u0627\u0631\u064a\u062e \u0627\u0644\u062a\u0623\u062c\u064a\u0644',
        deleteConfirm: '\u0647\u0644 \u0623\u0646\u062a \u0645\u062a\u0623\u0643\u062f \u0623\u0646\u0643 \u062a\u0631\u064a\u062f \u062d\u0630\u0641 \u0647\u0630\u0627 \u0627\u0644\u062d\u062c\u0632\u061f',
        deleteError: '\u062a\u0639\u0630\u0631 \u062d\u0630\u0641 \u0627\u0644\u062d\u062c\u0632',
        clientFallback: '\u0639\u0645\u064a\u0644',
        bookingFallback: '\u062d\u062c\u0632',
        vaccinationFallback: '\u062a\u0637\u0639\u064a\u0645',
        petFallback: '\u062d\u064a\u0648\u0627\u0646',
      };
    }

    if (this.isFr) {
      return {
        calendar: 'Calendrier',
        totalGoods: 'Cout total des marchandises',
        currency: 'EGP',
        title: 'Calendrier de la clinique',
        subtitle: 'Vue mensuelle des horaires d\'ouverture et de l\'activite planifiee de la clinique.',
        newOrder: 'Nouvelle facture',
        supply: 'Changement d\'urgence',
        accounts: 'Comptes',
        newBooking: 'Nouvelle reservation',
        refresh: 'Actualiser',
        monthHint: 'Chaque jour affiche ses horaires de travail',
        availableHours: 'Horaires disponibles',
        loading: 'Chargement des horaires de la clinique...',
        today: 'Aujourd\'hui',
        closed: 'Ferme',
        previousMonth: 'Mois precedent',
        currentMonth: 'Mois actuel',
        nextMonth: 'Mois suivant',
        bookings: 'Reservations clients',
        orders: 'Commandes',
        totalInvoicesMoney: 'Total des ventes',
        invoiceNumber: 'No. de facture',
        invoiceAmount: 'Montant de la facture',
        orderTime: 'Heure de commande',
        invoice: 'Facture',
        invoiceDate: 'Date de facture',
        customer: 'Client',
        phoneLabel: 'Telephone',
        invoiceItems: 'Articles de la facture',
        product: 'Produit',
        service: 'Service',
        productOrServiceSearchPlaceholder: 'Nom du produit ou du service',
        productFallback: 'Produit',
        quantityLabel: 'Qte',
        priceLabel: 'Prix',
        lineTotalLabel: 'Total',
        subtotalLabel: 'Sous-total',
        discountLabel: 'Remise',
        deliveryFeesLabel: 'Frais de livraison',
        paidLabel: 'Paye',
        remainingLabel: 'Restant',
        noInvoiceItems: 'Aucun article detaille dans la facture',
        vaccinations: 'Vaccinations',
        shifts: 'Shifts',
        shiftTime: 'Horaire du shift',
        shiftEmployees: 'Personnel en service',
        employeesCount: 'Employes',
        shiftSales: 'Ventes',
        shiftExpenses: 'Depenses',
        shiftProfit: 'Benefice net',
        noShiftEmployees: 'Aucun employe assigne a ce shift',
        employeeDetails: 'Details employe',
        employeeSales: 'Ventes employe',
        pending: 'En attente',
        approved: 'Approuve',
        completed: 'Termine',
        deferred: 'Reporte',
        changeDeferredDate: 'Modifier la date de report',
        rejected: 'Rejete',
        delete: 'Supprimer',
        close: 'Fermer',
        submit: 'Enregistrer',
        loadingAction: 'Chargement...',
        deferredDate: 'Date de report',
        noClinicId: 'ID de la clinique / entrepot introuvable.',
        openingHoursError: 'Une erreur est survenue lors du chargement des horaires de la clinique.',
        updateStatusError: 'Impossible de mettre a jour le statut de la reservation',
        updateDeferredError: 'Impossible de mettre a jour la date de report',
        deleteConfirm: 'Voulez-vous vraiment supprimer cette reservation ?',
        deleteError: 'Impossible de supprimer la reservation',
        clientFallback: 'Client',
        bookingFallback: 'Reservation',
        vaccinationFallback: 'Vaccination',
        petFallback: 'Animal',
      };
    }

    return {
      calendar: 'Calendar',
      totalGoods: 'Total Cost of Goods',
      currency: 'EGP',
      title: 'Clinic Calendar',
      subtitle: 'Monthly view of clinic opening hours and scheduled activity.',
      newOrder: 'New Invoice',
      supply: 'Emergency Shift Change',
      accounts: 'Accounts',
      newBooking: 'New Booking',
      refresh: 'Refresh',
      monthHint: 'Each day shows its working hours',
      availableHours: 'Available Hours',
      loading: 'Loading clinic opening hours...',
      today: 'Today',
      closed: 'Closed',
      previousMonth: 'Previous Month',
      currentMonth: 'Current Month',
      nextMonth: 'Next Month',
      bookings: 'Client Bookings',
      orders: 'Orders',
      totalInvoicesMoney: 'Total sales',
      invoiceNumber: 'Invoice No.',
      invoiceAmount: 'Invoice amount',
      orderTime: 'Order time',
      invoice: 'Invoice',
      invoiceDate: 'Invoice date',
      customer: 'Customer',
      phoneLabel: 'Phone',
      invoiceItems: 'Invoice items',
      product: 'Product',
      service: 'Service',
      productOrServiceSearchPlaceholder: 'Product or service name',
      productFallback: 'Product',
      quantityLabel: 'Qty',
      priceLabel: 'Price',
      lineTotalLabel: 'Total',
      subtotalLabel: 'Subtotal',
      discountLabel: 'Discount',
      deliveryFeesLabel: 'Delivery fees',
      paidLabel: 'Paid',
      remainingLabel: 'Remaining',
      noInvoiceItems: 'No detailed invoice items',
      vaccinations: 'Vaccinations',
      shifts: 'Shifts',
      shiftTime: 'Shift Time',
      shiftEmployees: 'Working Staff',
      employeesCount: 'Employees',
      shiftSales: 'Sales',
      shiftExpenses: 'Expenses',
      shiftProfit: 'Net Profit',
      noShiftEmployees: 'No employees assigned to this shift',
      employeeDetails: 'Employee Details',
      employeeSales: 'Employee Sales',
      pending: 'Pending',
      approved: 'Approved',
      completed: 'Completed',
      deferred: 'Deferred',
      changeDeferredDate: 'Change Deferred Date',
      rejected: 'Rejected',
      delete: 'Delete',
      close: 'Close',
      submit: 'Submit',
      loadingAction: 'Loading...',
      deferredDate: 'Deferred Date',
      noClinicId: 'Clinic / warehouse ID was not found.',
      openingHoursError: 'An error occurred while loading clinic opening hours.',
      updateStatusError: 'Failed to update booking status',
      updateDeferredError: 'Failed to update deferred date',
      deleteConfirm: 'Are you sure you want to delete this booking?',
      deleteError: 'Failed to delete booking',
      clientFallback: 'Client',
      bookingFallback: 'Booking',
      vaccinationFallback: 'Vaccination',
      petFallback: 'Pet',
    };
  }
}
